import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import st from './DealHistory.module.scss'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import useLocalization from 'hooks/useLocalization'
import Loader from 'components/UI/Loader/Loader'
import LocalStorage from 'services/LocalStorage'
import { superAxiosRequest } from 'axiosApi'
import { observer } from 'mobx-react'

const formatDateAndTime = (timestamp, lang = 'en') => {
  const date = new Date(timestamp)
  let locale
  if (lang === 'ru') {
    locale = 'ru-RU'
  } else if (lang === 'kz' || lang === 'kk') {
    locale = 'kk-KZ'
  } else {
    locale = 'en-US'
  }
  const time = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const day = date.getDate()
  const month = date.toLocaleString(locale, { month: 'long' })
  const year = date.getFullYear()
  const formattedDate = `${day} ${month} ${year}`
  return { time, formattedDate }
}

const formatCreatedAt = (timestamp, lang = 'en') => {
  const date = new Date(timestamp)
  let locale
  if (lang === 'ru') {
    locale = 'ru-RU'
  } else if (lang === 'kz' || lang === 'kk') {
    locale = 'kk-KZ'
  } else {
    locale = 'en-US'
  }
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

const DealHistory = () => {
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const ud = LocalStorage.get('ud')
  const user_data = ud?.user_data
  const userData = user_data?.profile
  const lang = userData?.localization.toLowerCase()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const funnelId = +searchParams.get('funnel_id')

  const [filter, updateFilter] = useLocalStorageFilter()
  const history = useHistory()
  const { id: dealId } = useParams()

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [dealData, setDealData] = useState(null)

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'all_groups',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',
      deal_history: 'Deal History',
      date_of_creation: 'Date of creation',
      search_by_employee: 'Search by employee',
      status: 'Status',
      status_successfully: 'Successfully',
    },
  })

  const filterCfg = [
    {
      nameState: 'employee',
      type: 'input',
      placeholder: translate.search_by_employee,
      label: translate.search_by_employee,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: translate.date_of_creation,
    },
  ]

  const fetchDealData = () => {
    superAxiosRequest({
      link: `integration/deals/${dealId}`,
      method: 'get',
      crm: true,
    })
      .then((res) => {
        setDealData(res.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const getDealCreator = () => {
    if (!dealData) return null
    if (!dealData.created_by) return 'Система'
    const { first_name, last_name } = dealData.created_by
    return `${first_name || ''} ${last_name || ''}`.trim() || 'Система'
  }

  useEffect(() => {
    setIsLoading(true)
    Promise.all([
      fetchDealData(),
      superAxiosRequest({
        link: `integration/deal_logs/${dealId}`,
        method: 'get',
        crm: true,
        params: {
          ...filter,
          lang,
          funnel_id: funnelId,
        },
      }),
    ])
      .then(([, logsResponse]) => {
        setData(logsResponse.data)
      })
      .catch((er) => {
        console.log(er)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [filter, dealId, funnelId, lang])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (
        location.pathname !==
        `/crm/deal-history/${dealId}/?funnel_id=${funnelId}`
      ) {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter, dealId, funnelId])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  const isDealSuccessfully = data?.filter((log) => log.is_successful)

  const mappedLogs = data
    ?.filter((log) => !log.is_successful)
    .map((log, index) => {
      const { time, formattedDate } = formatDateAndTime(log.timestamp, lang)

      const processedLogText =
        log.log_text?.replace(
          /(«([^»]*)»|"([^"]*)")/g,
          (match, textWithQuotes, textInsideQuotes1, textInsideQuotes2) => {
            const textInsideQuotes = textInsideQuotes1 || textInsideQuotes2

            if (textInsideQuotes?.length > 30) {
              const truncatedText = textInsideQuotes.slice(0, 30) + '...'

              if (match.startsWith('«')) {
                return `«${truncatedText}»`
              } else {
                return `"${truncatedText}"`
              }
            }

            return match
          },
        ) || ''

      return (
        <div
          key={`${log.timestamp}${log.log_text}`}
          className={`${st.logItem} ${index % 2 === 0 ? st.even : st.odd}`}
        >
          <div className={st.logText}>{processedLogText}</div>
          <div className={st.logDetails}>
            <span className={st.time}>{time}</span>
            <span className={st.date}>{formattedDate}</span>
          </div>
        </div>
      )
    })

  const dealCreator = getDealCreator()
  const dealCreatedAt = dealData?.created_at
    ? formatCreatedAt(dealData.created_at, lang)
    : null

  return (
    <div className={st.container}>
      <div className={st.header}>
        <PageHeader title={translate.deal_history}></PageHeader>
        <div className={st.searchFilterValues}>
          <SearchBooks
            onSearch={(text) => {
              updateFilter({ ...filter, employee: text })
              changeTextValue('employee', {
                key: 'employee',
                label: translate.search,
                value: text,
              })
            }}
            onClear={() => {
              const { search, ...obj } = filter
              updateFilter(obj)
              setTextValues(
                filterTextValues.filter((field) => field.key !== 'search'),
              )
            }}
            translate={translate}
            placeholder={translate.search}
            value={filter?.search || ''}
          />
          {!!filterTextValues.length && (
            <FilterTextValues
              onDelete={(key) => {
                const { [key]: _, ...obj } = filter
                updateFilter(obj)
              }}
            />
          )}
          {isOpenFilter && (
            <AsideFilter
              getFilterParams={(e) => {
                updateFilter(e)
              }}
              config={filterCfg}
              filterValue={filter}
              onDropFilter={() => {
                updateFilter({})
                closeFilter()
              }}
              translate={translate}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className={st.dealLogs}>
          {!!isDealSuccessfully?.length && (
            <div className={st.successfully}>
              <div className={st.textLog}>
                {isDealSuccessfully[0]?.log_text}
              </div>
              <div className={st.status}>
                <span>{translate.status}</span>
                <span>"{isDealSuccessfully[0]?.value_text}"</span>
              </div>
            </div>
          )}

          {mappedLogs?.length > 0 ? (
            <>
              {mappedLogs}
              <div className={st.dealInfo}>
                <div className={st.dealCreatedAt}>
                  <span className={st.label}>Время создания сделки:</span>
                  <span>{dealCreatedAt || '—'}.</span>
                  <span className={st.label}>Пользователь:</span>
                  <span>{dealCreator || '—'}</span>
                </div>
              </div>
            </>
          ) : (
            <div className={st.noHistory}>
              <p>Deal history not found</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default observer(DealHistory)
